import { useAuthFetch } from '~/composables/useAuthFetch'
import { useAuthClient } from '~/utils/auth-client'

export interface User {
  id: string
  email: string
  name: string
  role?: 'admin' | 'planta' | 'tienda'
  locationId?: string | null
  banned?: boolean
  createdAt?: Date | string
  updatedAt?: Date | string
  emailVerified?: boolean
  image?: string | null
  [key: string]: unknown
}

/**
 * Composable de autenticación para páginas con SSR
 * Usa useSession con useAuthFetch para mantener la sesión en hard refreshes
 *
 * IMPORTANTE: useAuthFetch llama al API absoluto y reenvía cookies en SSR
 */
export async function useAuthSSR() {
  const authClient = useAuthClient()
  const { data: sessionData } = await authClient.useSession(useAuthFetch)
  const user = computed(() => sessionData.value?.user as User | undefined)
  const session = computed(() => sessionData.value?.session)
  const isLoading = computed(() => !sessionData.value)
  const error = computed(() => null)

  return {
    user,
    session,
    isLoading,
    error
  }
}

/**
 * Composable de autenticación para componentes
 * Proporciona acceso a las funciones de autenticación de Better Auth
 *
 * ⚠️  ADVERTENCIA: Este composable NO funciona correctamente en páginas con SSR
 * Para páginas con SSR, usa useAuthSSR() en su lugar
 */
export function useAuth() {
  const authClient = useAuthClient()
  const sessionResponse = authClient.useSession()

  const user = computed(() => sessionResponse.value.data?.user as User | null)
  const session = computed(() => sessionResponse.value.data?.session)
  const isLoading = computed(() => sessionResponse.value.isPending)
  const error = computed(() => sessionResponse.value.error?.message || null)

  async function signIn(email: string, password: string, rememberMe = true) {
    try {
      const result = await authClient.signIn.email({
        email,
        password,
        rememberMe
      })

      if (result.error) {
        return { success: false, error: result.error.message || 'Error al iniciar sesión' }
      }

      return { success: true, user: result.data?.user }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al iniciar sesión'
      return { success: false, error: message }
    }
  }

  async function signUp(email: string, password: string, name: string) {
    try {
      const result = await authClient.signUp.email({
        email,
        password,
        name
      })

      if (result.error) {
        return { success: false, error: result.error.message || 'Error al registrarse' }
      }

      return { success: true, user: result.data?.user }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al registrarse'
      return { success: false, error: message }
    }
  }

  async function signOut() {
    try {
      await authClient.signOut()
      return { success: true }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al cerrar sesión'
      return { success: false, error: message }
    }
  }

  async function requestPasswordReset(email: string, redirectTo: string) {
    try {
      const result = await authClient.requestPasswordReset({
        email,
        redirectTo
      })

      if (result.error) {
        return { success: false, error: result.error.message || 'Error al enviar el correo' }
      }

      return { success: true }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al enviar el correo'
      return { success: false, error: message }
    }
  }

  async function resetPassword(password: string, token: string) {
    try {
      const result = await authClient.resetPassword({
        newPassword: password,
        token
      })

      if (result.error) {
        return { success: false, error: result.error.message || 'Error al resetear la contraseña' }
      }

      return { success: true }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al resetear la contraseña'
      return { success: false, error: message }
    }
  }

  const isAuthenticated = computed(() => !!user.value)

  return {
    user,
    session,
    isLoading,
    error,
    isAuthenticated,
    signIn,
    signUp,
    signOut,
    requestPasswordReset,
    resetPassword
  }
}
